/** @graphql cc **/
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import { gql } from 'graphql-tag';
import type { ComponentProps } from 'react';
import { useState } from 'react';

import type SmartUserPicker from '@atlaskit/smart-user-picker';

import type { OptionData } from '@confluence/user-and-group-search/entry-points/user-picker';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';

import type {
	useAccessClassTransformerQuery as useAccessClassTransformerQueryType,
	useAccessClassTransformerQueryVariables,
} from './__types__/useAccessClassTransformerQuery';

const useAccessClassTransformerQuery = gql`
	query useAccessClassTransformerQuery($cloudId: ID!) {
		confluence_userClasses(cloudId: $cloudId) {
			nodes {
				principalId
				displayName
			}
		}
	}
`;

const toLowerWithoutSpaces = (str: string) => str?.toLowerCase().replace(/ /g, '');

interface UseAccessClassTransformerResult {
	setSearchQuery: (searchQuery: string) => void;
	transformOptions: ComponentProps<typeof SmartUserPicker>['transformOptions'];
}

export const useAccessClassTransformer = (): UseAccessClassTransformerResult => {
	const { cloudId } = useSessionData();
	const { data } = useQuery<
		useAccessClassTransformerQueryType,
		useAccessClassTransformerQueryVariables
	>(useAccessClassTransformerQuery, {
		variables: { cloudId },
		onError: (e) => {
			getMonitoringClient().submitError(e, {
				attribution: Attribution.PERMISSIONS_EXPERIENCE,
			});
			markErrorAsHandled(e);
		},
	});
	const [searchQuery, setSearchQuery] = useState('');

	const allConfluenceUsers = data?.confluence_userClasses?.nodes?.find(
		(userClass) => userClass?.principalId === 'all-licensed-users',
	);

	const allConfluenceAdmins = data?.confluence_userClasses?.nodes?.find(
		(userClass) => userClass?.principalId === 'all-product-admins',
	);

	const transformOptions = async (options: OptionData[]) => {
		const cleanedSearchTerm = toLowerWithoutSpaces(searchQuery);
		const customInclusionList: OptionData[] = [];
		[allConfluenceUsers, allConfluenceAdmins].forEach((userClass) => {
			if (
				userClass &&
				toLowerWithoutSpaces(userClass.displayName || '').includes(cleanedSearchTerm)
			) {
				customInclusionList.push({
					id: userClass.principalId,
					name: userClass.displayName,
					type: 'custom' as const,
					avatarUrl: require('./images/atlassian-icon.svg'),
				});
			}
		});
		return [...options, ...customInclusionList];
	};

	return {
		setSearchQuery,
		transformOptions,
	};
};
